import {
  MutationGenqlSelection,
  Mutation,
  FieldsSelection,
} from '@rematter/frontend-lib-api-client';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import camelCase from 'lodash/camelCase';

import { useApi } from '~/shared/hooks';

export type MutationHookOptions<TData, TVariables, TResult> = Omit<
  UseMutationOptions<TData, Error, TVariables, TResult>,
  'mutationKey' | 'mutationFn'
> & {
  alias?: string;
  select?: (data: TResult) => TData;
};

export type MutationHookResult<TData, TVariables> = UseMutationResult<
  TData,
  Error,
  TVariables
>;

/**
 * @deprecated use `createMutationAtomFactory` instead
 */
export function createMutationHook<
  TVariables,
  TSelection extends MutationGenqlSelection,
  TData = FieldsSelection<Mutation, TSelection>,
  TResult = TData,
>(
  key: `${string}.${string}`, // 'transportation.truckCreate'
  selectionFn: (variables: TVariables) => TSelection,
  options?: MutationHookOptions<TData, TVariables, TResult> & {
    invalidateQueries?: { __queryKey: string }[];
  },
) {
  return function useGenqlMutation(
    mutationOptions?: Omit<
      UseMutationOptions<TData, Error, TVariables, TResult>,
      'mutationKey' | 'mutationFn'
    >,
  ): MutationHookResult<TData, TVariables> {
    const { client } = useApi();

    const mutationKey = [options?.alias || key];

    const queryClient = useQueryClient();

    return useMutation<TData, Error, TVariables, TResult>({
      mutationKey,
      mutationFn: async (variables) => {
        try {
          const selection = selectionFn(variables);

          const result = await client(`mutation.${key}`).mutation({
            ...selection,
            __name: camelCase(options?.alias || key),
          });

          options?.invalidateQueries?.forEach((query) =>
            queryClient.invalidateQueries({
              queryKey: [query.__queryKey],
            }),
          );

          if (options?.select) {
            return options.select(result as TResult);
          }

          return result as TData;
        } catch (error) {
          throw error instanceof Error ? error : new Error(String(error));
        }
      },
      ...options,
      ...mutationOptions,
    });
  };
}
